import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "menu", "options", "selectedList", "hiddenInputs", "trigger", "triggerLabel"]
  static values = {
    options: Array,
    selected: Array,
    inputName: String,
    placeholder: String,
    selectedLabelSingular: { type: String, default: "input" },
    selectedLabelPlural: { type: String, default: "inputs" },
    emptyLabel: { type: String, default: "No matching inputs." },
    defaultGroup: { type: String, default: "Inputs" },
    filterMode: String
  }

  connect() {
    this.pruneSelectedForFilter()
    this.renderSelected()
    this.renderOptions()
    this.setExpanded(false)
    document.addEventListener("click", this.closeIfOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.closeIfOutside)
  }

  closeIfOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  filter() {
    this.renderOptions()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    }
  }

  select(event) {
    event.preventDefault()

    const id = event.currentTarget.dataset.optionId || event.currentTarget.dataset.inputSourceId
    const option = this.optionsValue.find((item) => item.id === id)
    if (!option) return

    this.selectedValue = [...this.selectedValue, option]
    this.inputTarget.value = ""
    this.renderSelected()
    this.renderOptions()
    this.dispatchChange()
    this.inputTarget.focus()
  }

  remove(event) {
    event.preventDefault()

    const id = event.currentTarget.dataset.optionId || event.currentTarget.dataset.inputSourceId
    this.selectedValue = this.selectedValue.filter((option) => option.id !== id)
    this.renderSelected()
    this.renderOptions()
    this.dispatchChange()
  }

  filterModeValueChanged() {
    if (!this.hasOptionsTarget) return

    this.pruneSelectedForFilter()
    this.renderSelected()
    this.renderOptions()
    this.dispatchChange()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.setExpanded(true)
    this.renderOptions()
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.setExpanded(false)
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.renderOptions()
  }

  renderSelected() {
    this.selectedListTarget.innerHTML = this.selectedValue.map((option) => `
      <span class="work-builder-picker__chip">
        <span class="truncate">${this.escapeHtml(option.name)}</span>
        <button type="button"
                data-action="workflow-input-select#remove"
                data-option-id="${this.escapeHtml(option.id)}"
                aria-label="Remove ${this.escapeHtml(option.name)}">
          <span aria-hidden="true">&times;</span>
        </button>
      </span>
    `).join("")

    this.hiddenInputsTarget.innerHTML = `
      <input type="hidden" name="${this.escapeHtml(this.inputNameValue)}" value="">
      ${this.selectedValue.map((option) => `
        <input type="hidden" name="${this.escapeHtml(this.inputNameValue)}" value="${this.escapeHtml(option.id)}">
      `).join("")}
    `

    const count = this.selectedValue.length
    if (count === 0) {
      this.triggerLabelTarget.textContent = this.placeholderValue
    } else {
      const label = count === 1 ? this.selectedLabelSingularValue : this.selectedLabelPluralValue
      this.triggerLabelTarget.textContent = `${count} ${label} selected`
    }
  }

  renderOptions() {
    const query = this.hasInputTarget ? this.inputTarget.value.toLowerCase().trim() : ""
    const selectedIds = new Set(this.selectedValue.map((option) => option.id))
    const options = this.availableOptions().filter((option) => {
      if (selectedIds.has(option.id)) return false
      if (query.length === 0) return true

      return [
        option.name,
        option.group,
        option.id
      ].some((value) => value?.toLowerCase().includes(query))
    })

    if (options.length === 0) {
      this.optionsTarget.innerHTML = `
        <div class="work-builder-picker__empty">
          ${this.escapeHtml(this.emptyLabelValue)}
        </div>
      `
      return
    }

    this.optionsTarget.innerHTML = this.groupOptions(options).map(([group, groupOptions]) => `
      <div class="work-builder-picker__group">
        <div class="work-builder-picker__group-label">
          ${this.escapeHtml(group)}
        </div>
        ${groupOptions.map((option) => `
          <button type="button"
                  class="work-builder-picker__option"
                  data-action="workflow-input-select#select"
                  data-option-id="${this.escapeHtml(option.id)}">
            <span class="block text-sm font-medium text-[var(--color-ink)]">${this.escapeHtml(option.name)}</span>
          </button>
        `).join("")}
      </div>
    `).join("")
  }

  groupOptions(options) {
    const groups = new Map()

    options.forEach((option) => {
      const group = option.group || this.defaultGroupValue
      groups.set(group, [...(groups.get(group) || []), option])
    })

    return Array.from(groups.entries())
  }

  availableOptions() {
    return this.optionsValue.filter((option) => this.optionAllowedForFilter(option))
  }

  optionAllowedForFilter(option) {
    const mode = this.filterModeValue
    if (!mode) return true

    const modes = Array.isArray(option.modes) ? option.modes : []
    return modes.length === 0 || modes.includes("all") || modes.includes(mode)
  }

  pruneSelectedForFilter() {
    const allowedIds = new Set(this.availableOptions().map((option) => option.id))
    this.selectedValue = this.selectedValue.filter((option) => allowedIds.has(option.id))
  }

  dispatchChange() {
    this.dispatch("change", { detail: { selected: this.selectedValue } })
  }

  setExpanded(expanded) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    }
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }
}
